import React, { useState, useRef, useCallback, useEffect, useMemo } from 'react';
import { 
  Heart, 
  MessageCircle, 
  Share2, 
  Search, 
  Bell, 
  User, 
  Stethoscope, 
  Brain, 
  FlaskConical,
  Briefcase,
  TrendingUp,
  Plus,
  Shield,
  Activity,
  X,
  Eye,
  ChevronDown,
  ChevronUp,
  Settings,
  Pencil,
  Trash2,
  Copy,
  RotateCcw,
  MoreVertical,
  Link,
  Menu,
  BookOpen,
  Landmark,
  ArrowUp,
  Paperclip
} from 'lucide-react';
import './App.css';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { usePosts } from './hooks/usePosts';
import { ProfilePage } from './components/ProfilePage';
import ChannelsNav from './components/ChannelsNav';
import ChannelView from './components/ChannelView';
import SponsoredCard from './components/SponsoredCard';
import SponsorPortal from './components/SponsorPortal';
import SurveyPanel from './components/SurveyPanel';
import CaseGuide, { CASE_TEMPLATE } from './components/CaseGuide';
import { useGuestPosts } from './hooks/useGuestPosts';
import { aiAPI, commentsAPI, notificationsAPI, socialAPI, postsAPI, uploadAPI, apiKeysAPI, sponsorsAPI, AUTH_EXPIRED_EVENT } from './utils/api';
import { guestAPI } from './utils/guestApi';
import { formatRelativeTime } from './utils/timeUtils';
import { linkifyWithLineBreaks } from './utils/linkify';
import LoginModal from './components/LoginModal';
import SignupModal from './components/SignupModal';
import GuestMode from './components/GuestMode';
import ApiKeysModal from './components/ApiKeysModal';
import AdminDashboard from './components/AdminDashboard';
import AdminErrorBoundary from './components/AdminErrorBoundary';
import ApiDocsPage from './components/ApiDocsPage';
import { MarkdownRenderer } from './components/MarkdownRenderer';
import { decodeEntities, looksLikeMarkdown, toPlainExcerpt } from './utils/postContent';
import { LinkPreviewList } from './components/LinkPreview';
import { extractUrls, isUrlOnlyText, withoutUrlLines } from './utils/links';

const AppContent = () => {
  const { user, isAuthenticated, logout, loading: authLoading } = useAuth();
  const [activeTab, setActiveTab] = useState('feed');
  // 채널(슬랙식 워크스페이스) 사이드바 새로고침 신호
  const [channelRefresh, setChannelRefresh] = useState(0);
  // 피드 스폰서 카드 (문맥 타깃팅 — 로그인 사용자의 전문과목 기준, 게스트는 전체 대상)
  const [feedAd, setFeedAd] = useState<any>(null);
  useEffect(() => {
    sponsorsAPI
      .getFeedAds(user?.specialization, 1)
      .then((res) => setFeedAd(res.data.ads?.[0] || null))
      .catch(() => setFeedAd(null));
  }, [user?.specialization]);
  const [searchQuery, setSearchQuery] = useState('');
  // 검색은 입력이 멈춘 뒤 0.3초에 한 번 — 한글은 자모마다 입력 이벤트가 나서 글자마다 피드를 다시 불렀다
  const [debouncedSearch, setDebouncedSearch] = useState('');
  useEffect(() => {
    const timer = setTimeout(() => setDebouncedSearch(searchQuery.trim()), 300);
    return () => clearTimeout(timer);
  }, [searchQuery]);
  const [showPostModal, setShowPostModal] = useState(false);
  const [showLoginModal, setShowLoginModal] = useState(false);
  const [showSignupModal, setShowSignupModal] = useState(false);
  const [showProfileModal, setShowProfileModal] = useState(false);
  const [profileUserId, setProfileUserId] = useState<number | null>(null);
  const [aiQuery, setAiQuery] = useState('');
  const [aiResponse, setAiResponse] = useState('');
  const [aiLoading, setAiLoading] = useState(false);
  const [aiCopied, setAiCopied] = useState(false);
  const suggestedPrompts = [
    '고혈압 환자의 생활 관리 가이드',
    '만성 신부전 환자의 약물 조정 시 주의점',
    '뇌졸중 의심 환자 초기 평가 프로토콜',
    '소아 발열 환자 감별 진단 체크리스트'
  ];
  const [trendingOpen, setTrendingOpen] = useState(true);
  const [newPost, setNewPost] = useState({ title: '', content: '', type: 'discussion', tags: '' });
  // 증례 작성 모드 — 환자 동의·비식별 확인을 받아야 게시된다(서버에서도 강제)
  const [caseMode, setCaseMode] = useState(false);
  const [caseConsent, setCaseConsent] = useState(false);
  const [caseDeidentified, setCaseDeidentified] = useState(false);
  const [postError, setPostError] = useState('');
  const [postImages, setPostImages] = useState<File[]>([]);
  const [postImagePreviews, setPostImagePreviews] = useState<string[]>([]);
  const [postSubmitting, setPostSubmitting] = useState(false);
  const [likedPosts, setLikedPosts] = useState(new Set<number>());
  const [showEditModal, setShowEditModal] = useState(false);
  const [editingPostId, setEditingPostId] = useState<number | null>(null);
  const [editPost, setEditPost] = useState({ title: '', content: '', type: 'case_study', tags: '' });
  const [openCommentsPostId, setOpenCommentsPostId] = useState<number | null>(null);
  const [showPostDetailModal, setShowPostDetailModal] = useState(false);
  const [selectedPost, setSelectedPost] = useState<any>(null);
  const selectedPostRef = useRef<any>(null);
  selectedPostRef.current = selectedPost;
  const [showMoreMenu, setShowMoreMenu] = useState(false);
  const [commentInput, setCommentInput] = useState<string>('');
  const [comments, setComments] = useState<Record<number, any[]>>({});
  const [notifications, setNotifications] = useState<any[]>([]);
  const [notificationsOpen, setNotificationsOpen] = useState(false);
  const [showMobileMenu, setShowMobileMenu] = useState(false);
  const [showMobileRightBar, setShowMobileRightBar] = useState(false);
  const [showAiModal, setShowAiModal] = useState(false);
  const [aiImage, setAiImage] = useState<File | null>(null);
  const [aiImagePreview, setAiImagePreview] = useState<string | null>(null);
  const [aiConsultImageUrl, setAiConsultImageUrl] = useState<string | null>(null); // AI 상담에 사용된 이미지 URL 저장
  // 근거 기반 임상 Q&A (PubMed 인용) — 검증 의사 전용, 이미지 첨부 시 레거시 모드로 자동 전환
  const [aiEvidenceMode, setAiEvidenceMode] = useState(true);
  const [aiCitations, setAiCitations] = useState<any[]>([]);
  const [aiInsufficientEvidence, setAiInsufficientEvidence] = useState(false);
  const [apiKeysOpen, setApiKeysOpen] = useState(false);
  const [recentQueries, setRecentQueries] = useState<string[]>(() => {
    try {
      const raw = localStorage.getItem('ai_recent_queries');
      if (!raw) return [];
      const arr = JSON.parse(raw);
      return Array.isArray(arr) ? arr.slice(0, 10) : [];
    } catch {
      return [];
    }
  });

  // 현재 선택된 탭을 API type으로 매핑
  const apiType = activeTab === 'feed'
    ? undefined
    : activeTab === 'cases'
      ? 'case_study'
      : activeTab === 'jobs'
        ? 'job'
        : activeTab; // discussion, research, medical_dispute 그대로 사용
  
  // API 연동된 게시글 데이터 (인증된 사용자)
  const { 
    posts: authPosts, 
    loading: authPostsLoading, 
    error: authPostsError,
    loadMore: authLoadMore,
    retry: authRetry,
    hasMore: authHasMore,
    likePost, 
    createPost,
    updatePost,
    deletePost
  } = usePosts({
    type: apiType,
    searchQuery: debouncedSearch || undefined,
  }, !authLoading && isAuthenticated);

  // 게스트용 게시글 데이터
  const { 
    posts: guestPosts, 
    loading: guestPostsLoading,
    error: guestPostsError,
    loadMore: guestLoadMore,
    retry: guestRetry,
    hasMore: guestHasMore
  } = useGuestPosts({
    type: apiType,
    searchQuery: debouncedSearch || undefined,
  }, !authLoading && !isAuthenticated);
  // (인증 확인 중 게스트 피드를 먼저 그렸다가 바꾸면 화면이 한 번 튀었다)

  // 현재 사용할 데이터 결정
  const posts = isAuthenticated ? authPosts : guestPosts;
  const postsLoading = authLoading || (isAuthenticated ? authPostsLoading : guestPostsLoading);
  const postsError = isAuthenticated ? authPostsError : guestPostsError;
  const loadMore = isAuthenticated ? authLoadMore : guestLoadMore;
  const retryPosts = isAuthenticated ? authRetry : guestRetry;
  const hasMore = isAuthenticated ? authHasMore : guestHasMore;

  const observer = useRef<IntersectionObserver | null>(null);
  const loadMoreRef = useRef(loadMore);
  loadMoreRef.current = loadMore;  // 항상 최신 loadMore 유지
  
  // 마지막 카드가 화면 아래 600px 안에 들어오면 다음 페이지를 미리 부른다.
  // 실패하면 자동 재시도하지 않는다(실패 → 재관찰 → 즉시 재요청이 반복되며 서버를 두드렸다) — '다시 시도' 버튼으로.
  const lastPostElementRef = useCallback((node: HTMLDivElement | null) => {
    if (postsLoading) return;
    if (observer.current) observer.current.disconnect();
    if (!node || !hasMore || postsError) return;
    observer.current = new IntersectionObserver((entries: IntersectionObserverEntry[]) => {
      if (entries[0].isIntersecting) {
        loadMoreRef.current();  // ref를 통해 호출
      }
    }, { rootMargin: '0px 0px 600px 0px' });
    observer.current.observe(node);
  }, [postsLoading, hasMore, postsError]);

  const trendingTopics = [
    { tag: "AI진단", count: 234, trend: "up" },
    { tag: "심근경색", count: 156, trend: "stable" },
    { tag: "COVID19", count: 298, trend: "down" },
    { tag: "뇌종양", count: 87, trend: "up" }
  ];

  const simulateAIResponse = async (query: string) => {
    setAiLoading(true);
    try {
      if (isAuthenticated) {
        if (aiEvidenceMode && !aiImage) {
          // 근거 기반 임상 Q&A — PubMed 인용, 근거 없으면 무응답
          const response = await aiAPI.evidenceConsult(query);
          if (response.data.insufficientEvidence) {
            setAiResponse(response.data.message || '근거를 찾지 못했습니다.');
            setAiInsufficientEvidence(true);
          } else {
            setAiResponse(response.data.answer);
            setAiInsufficientEvidence(false);
          }
          setAiCitations(response.data.citations || []);
          setAiConsultImageUrl(null);
        } else {
          // 레거시 모드: Multipart 업로드 (이미지가 있을 때) 또는 텍스트만 전송
          const response = aiImage
            ? await aiAPI.consultWithImage(query, aiImage)
            : await aiAPI.consult(query);

          setAiResponse(response.data.response);
          setAiCitations([]);
          setAiInsufficientEvidence(false);

          // 업로드된 이미지 URL이 있으면 저장 (포스팅 시 사용)
          if (response.data.imageUrl) {
            console.log('[AI] 업로드된 이미지 URL:', response.data.imageUrl);
            setAiConsultImageUrl(response.data.imageUrl);
          } else {
            setAiConsultImageUrl(null);
          }
        }
      } else {
        const response = await guestAPI.consult(query);
        setAiResponse(response.data.response);
        setAiCitations([]);
        setAiInsufficientEvidence(false);
        setAiConsultImageUrl(null);
      }
      // 최근 질의 저장
      setRecentQueries(prev => {
        const next = [query, ...prev.filter(q => q !== query)].slice(0, 10);
        localStorage.setItem('ai_recent_queries', JSON.stringify(next));
        return next;
      });
      // 이미지 초기화
      setAiImage(null);
      setAiImagePreview(null);
    } catch (error: any) {
      console.error('[AI] AI 상담 오류:', error);
      // Heroku 라우터 30초 타임아웃(H12)은 JSON 없는 503으로 떨어짐 — 타임아웃임을 구분해 안내
      const serverMsg = error?.response?.data?.error;
      const timedOut = error?.response?.status === 503 && !serverMsg;
      setAiResponse(serverMsg || (timedOut
        ? '요청 처리 시간이 초과되었습니다(30초 제한). 잠시 후 다시 시도해주세요 — 반복되면 질문을 짧게 나눠주세요.'
        : 'AI 서비스에 일시적인 문제가 발생했습니다. 잠시 후 다시 시도해주세요.'));
      setAiCitations([]);
      setAiInsufficientEvidence(false);
      setAiConsultImageUrl(null);
    } finally {
      setAiLoading(false);
    }
  };

  const handleAiImageUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // 파일 크기 제한 (10MB - 백엔드에서 증가됨)
    if (file.size > 10 * 1024 * 1024) {
      alert('이미지 크기는 10MB를 초과할 수 없습니다.');
      return;
    }

    // 이미지 타입 확인
    if (!file.type.startsWith('image/')) {
      alert('이미지 파일만 업로드 가능합니다.');
      return;
    }

    // File 객체 저장 (Multipart 전송용)
    setAiImage(file);

    // 미리보기 생성
    const reader = new FileReader();
    reader.onload = (e) => {
      setAiImagePreview(e.target?.result as string);
    };
    reader.readAsDataURL(file);
    
    console.log('[AI] 이미지 선택됨:', file.name, `(${(file.size / 1024 / 1024).toFixed(2)}MB)`);
  };

  const handleAIConsult = async () => {
    if (aiQuery.trim()) {
      await simulateAIResponse(aiQuery);
    }
  };

  const clearAIInput = () => {
    setAiQuery('');
    setAiResponse('');
    setAiConsultImageUrl(null);
    setAiImage(null);
    setAiImagePreview(null);
  };

  const copyAIResponse = async () => {
    if (!aiResponse) return;
    try {
      await navigator.clipboard.writeText(aiResponse);
      setAiCopied(true);
      setTimeout(() => setAiCopied(false), 2000); // 2초 후 원래 상태로
    } catch (e) {
      console.error('복사 실패:', e);
      alert('복사에 실패했습니다.');
    }
  };

  // AI 상담 결과를 포스팅으로 변환
  const handlePostFromAI = async () => {
    if (!aiQuery || !aiResponse || !isAuthenticated) {
      alert('AI 상담 결과가 없거나 로그인이 필요합니다.');
      return;
    }

    setPostSubmitting(true);
    try {
      // AI 질문을 제목으로, 응답을 내용으로 사용 (근거 모드면 인용 문헌 목록 포함)
      const citationsBlock = aiCitations.length > 0
        ? `\n\n**인용 문헌:**\n${aiCitations
            .map((c: any) => `[${c.n}] ${c.title} — ${c.journal} (${c.year}) ${c.url}`)
            .join('\n')}`
        : '';
      const postData = {
        type: 'case_study', // 기본값은 사례 연구
        title: aiQuery.length > 100 ? aiQuery.substring(0, 100) + '...' : aiQuery,
        content: `**질문:** ${aiQuery}\n\n**AI 분석 결과:**\n\n${aiResponse}${citationsBlock}`,
        tags: [], // 필요시 AI 응답에서 태그 추출 가능
        images: aiConsultImageUrl ? [aiConsultImageUrl] : []
      };

      await createPost(postData);
      
      // 성공 메시지
      alert('AI 상담 결과가 포스팅되었습니다! ✨');
      
      // AI 모달 닫기 및 상태 초기화
      setShowAiModal(false);
      setAiQuery('');
      setAiResponse('');
      setAiConsultImageUrl(null);
      setAiImage(null);
      setAiImagePreview(null);
      
      // 피드로 이동하여 새 포스팅 확인
      setActiveTab('feed');
    } catch (error: any) {
      console.error('[AI] 포스팅 실패:', error);
      alert(`포스팅에 실패했습니다: ${error.response?.data?.error || error.message}`);
    } finally {
      setPostSubmitting(false);
    }
  };

  // 서버가 알려 준 좋아요 상태를 화면 상태에 반영(목록을 새로 받을 때마다)
  useEffect(() => {
    if (!isAuthenticated) return;
    setLikedPosts(prev => {
      const next = new Set(prev);
      authPosts.forEach(p => (p.liked ? next.add(p.id) : next.delete(p.id)));
      return next;
    });
  }, [authPosts, isAuthenticated]);

  const likeInFlight = useRef(new Set<number>());
  const handleLike = async (postId: number) => {
    // 요청 중 연속 클릭은 무시 — 두 번 눌러 켜졌다 꺼지거나 수가 어긋나던 문제
    if (likeInFlight.current.has(postId)) return;
    likeInFlight.current.add(postId);

    // 낙관적 업데이트: UI에 즉시 반영
    const wasLiked = likedPosts.has(postId);
    setLikedPosts(prev => {
      const next = new Set(prev);
      if (wasLiked) next.delete(postId);
      else next.add(postId);
      return next;
    });

    try {
      // 서버 응답(실제 상태·개수)으로 확정 — usePosts의 likePost가 posts 배열도 업데이트
      const { liked, likes } = await likePost(postId);
      setLikedPosts(prev => {
        const next = new Set(prev);
        if (liked) next.add(postId);
        else next.delete(postId);
        return next;
      });
      setSelectedPost((prev: any) => (prev && prev.id === postId ? { ...prev, likes, liked } : prev));
    } catch (error) {
      console.error('좋아요 처리 실패:', error);
      // 실패 시 롤백
      setLikedPosts(prev => {
        const next = new Set(prev);
        if (wasLiked) next.add(postId);
        else next.delete(postId);
        return next;
      });
    } finally {
      likeInFlight.current.delete(postId);
    }
  };

  // 댓글은 항상 이 함수로 불러온다(같은 개수 기준). 예전엔 열 때 50개·등록 후 20개라
  // 댓글이 20개를 넘는 글에서 방금 쓴 댓글이 목록에서 빠지고 개수도 줄었다.
  const COMMENT_LIMIT = 100;
  const loadComments = async (postId: number) => {
    try {
      const res = await commentsAPI.list({ postId, page: 1, limit: COMMENT_LIMIT });
      setComments(prev => ({ ...prev, [postId]: res.data.comments || [] }));
    } catch (e) {
      console.error('댓글 불러오기 실패:', e);
    }
  };

  const toggleComments = async (postId: number) => {
    const isOpen = openCommentsPostId === postId;
    setOpenCommentsPostId(isOpen ? null : postId);
    if (!isOpen) loadComments(postId);
  };

  const [commentSubmitting, setCommentSubmitting] = useState(false);
  const commentInFlight = useRef(false); // state는 다음 렌더 전까지 그대로라 같은 순간의 연속 클릭을 못 막는다
  const submitComment = async (postId: number) => {
    // 등록 중 Enter·버튼을 다시 누르면 같은 댓글이 두 번 올라갔다
    if (!commentInput.trim() || commentInFlight.current) return;
    commentInFlight.current = true;
    setCommentSubmitting(true);
    try {
      await commentsAPI.create({ postId, content: commentInput.trim() });
      setCommentInput('');
      await loadComments(postId);
    } catch (e) {
      console.error('댓글 작성 실패:', e);
    } finally {
      commentInFlight.current = false;
      setCommentSubmitting(false);
    }
  };

  const deleteComment = async (commentId: number, postId: number) => {
    if (!window.confirm('댓글을 삭제하시겠습니까?')) return;
    try {
      await commentsAPI.remove(commentId);
      await loadComments(postId);
    } catch (e) {
      console.error('댓글 삭제 실패:', e);
      alert('댓글 삭제에 실패했습니다.');
    }
  };

  // 알림 로드
  const loadNotifications = async () => {
    if (!isAuthenticated || document.hidden) return;
    try {
      const res = await notificationsAPI.list({ page: 1, limit: 20 });
      const next = res.data.notifications || [];
      setNotifications(prev => (JSON.stringify(prev) === JSON.stringify(next) ? prev : next));
    } catch (e) {
      console.error('알림 불러오기 실패:', e);
    }
  };

  // 페이지 로드 시 알림 자동 로딩
  useEffect(() => {
    if (isAuthenticated) {
      loadNotifications();
      // 30초마다 알림 갱신(숨긴 탭에서는 건너뛰고, 탭으로 돌아오면 바로 갱신)
      const interval = setInterval(loadNotifications, 30000);
      const onVisible = () => {
        if (!document.hidden) loadNotifications();
      };
      document.addEventListener('visibilitychange', onVisible);
      return () => {
        clearInterval(interval);
        document.removeEventListener('visibilitychange', onVisible);
      };
    }
  }, [isAuthenticated]);

  // 알림 드롭다운 외부 클릭 시 닫기
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as HTMLElement;
      if (notificationsOpen && !target.closest('.notification-dropdown-container')) {
        setNotificationsOpen(false);
      }
    };

    if (notificationsOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [notificationsOpen]);

  // URL에서 postId 파라미터를 읽어 게시글 모달 열기
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const postId = urlParams.get('postId');
    
    if (postId) {
      // 게시글 로드 후 모달 열기
      const loadPostFromUrl = async () => {
        // 성공·실패와 무관하게 파라미터를 지운다(실패 시 새로고침마다 알림창이 반복됐다)
        window.history.replaceState({}, '', window.location.pathname);
        try {
          const res = await postsAPI.getPost(Number(postId));
          // 그 사이 사용자가 다른 글을 열었다면 덮어쓰지 않는다
          if (!selectedPostRef.current) openPostDetail(res.data, { viewRecorded: true });
        } catch (error: any) {
          console.error('URL에서 게시글 로드 실패:', error);
          if (error?.response?.status === 404) alert('게시글을 찾을 수 없습니다.');
        }
      };
      
      loadPostFromUrl();
    }
  }, []);

  const toggleNotifications = () => {
    setNotificationsOpen(!notificationsOpen);
  };

  // 알림 클릭 처리
  const handleNotificationClick = async (notification: any) => {
    // 읽음 처리
    if (!notification.isRead) {
      try {
        await notificationsAPI.markRead(notification.id);
        setNotifications(prev =>
          prev.map(n => n.id === notification.id ? { ...n, isRead: true } : n)
        );
      } catch (e) {
        console.error('알림 읽음 처리 실패:', e);
      }
    }

    // 해당 게시글로 이동
    if (notification.refType === 'post' && notification.refId) {
      try {
        const res = await postsAPI.getPost(notification.refId);
        setNotificationsOpen(false);
        // 댓글 알림으로 열었는데 댓글이 0개로 보이던 문제 — 피드에서 열 때와 같은 경로로 연다
        openPostDetail(res.data, { viewRecorded: true });
      } catch (e) {
        console.error('게시글 로드 실패:', e);
      }
    }
  };

  // 읽지 않은 알림 수
  const unreadCount = notifications.filter(n => !n.isRead).length;

  const handleNewPost = async () => {
    if (newPost.content) {
      setPostSubmitting(true);
      try {
        // 이미지 업로드 (있는 경우)
        let imageUrls: string[] = [];
        if (postImages.length > 0) {
          try {
            const uploadResponse = await uploadAPI.uploadImages(postImages, 'posts');
            imageUrls = (uploadResponse as any).data?.urls || (uploadResponse as any).data?.imageUrls || [];
            
            if (imageUrls.length === 0) {
              throw new Error('이미지 업로드에 실패했습니다. URL을 받지 못했습니다.');
            }
            
            console.log(`[POST] ${imageUrls.length}개 이미지 업로드 성공:`, imageUrls);
          } catch (uploadError: any) {
            console.error('[POST] 이미지 업로드 실패:', uploadError);
            alert(`이미지 업로드에 실패했습니다: ${uploadError.response?.data?.error || uploadError.message}`);
            setPostSubmitting(false);
            return; // 업로드 실패 시 게시 중단
          }
        }

        await createPost({
          type: caseMode ? 'case_study' : newPost.type,
          title: newPost.title,
          content: newPost.content,
          tags: newPost.tags ? newPost.tags.split(',').filter(Boolean).map(tag => tag.trim()) : [],
          images: imageUrls,
          ...(caseMode ? { case_consent: caseConsent, case_deidentified: caseDeidentified } : {})
        });
        setShowPostModal(false);
        setNewPost({ title: '', content: '', type: 'discussion', tags: '' });
        setPostImages([]);
        setPostImagePreviews([]);
        setCaseMode(false);
        setCaseConsent(false);
        setCaseDeidentified(false);
        setPostError('');
        // createPost가 자동으로 fetchPosts(true)를 호출하여 피드 최상단에 새 게시글이 표시됨
      } catch (error: any) {
        console.error('게시글 작성 실패:', error);
        // 서버가 알려준 이유(식별정보 차단 등)를 그대로 보여준다 — 무엇을 고쳐야 할지 알 수 있게.
        setPostError(error?.message || '게시글 작성에 실패했습니다. 다시 시도해주세요.');
      } finally {
        setPostSubmitting(false);
      }
    }
  };

  const handlePostImageUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files || []);
    if (files.length === 0) return;

    // 최대 5개 제한
    const totalImages = postImages.length + files.length;
    if (totalImages > 5) {
      alert('이미지는 최대 5개까지 업로드할 수 있습니다.');
      return;
    }

    // 파일 크기 검증 (각 10MB)
    for (const file of files) {
      if (file.size > 10 * 1024 * 1024) {
        alert(`${file.name}의 크기가 10MB를 초과합니다.`);
        return;
      }
      if (!file.type.startsWith('image/')) {
        alert(`${file.name}은(는) 이미지 파일이 아닙니다.`);
        return;
      }
    }

    // 프리뷰 생성
    const newPreviews: string[] = [];
    files.forEach(file => {
      const reader = new FileReader();
      reader.onload = (e) => {
        newPreviews.push(e.target?.result as string);
        if (newPreviews.length === files.length) {
          setPostImagePreviews(prev => [...prev, ...newPreviews]);
        }
      };
      reader.readAsDataURL(file);
    });

    setPostImages(prev => [...prev, ...files]);
  };

  const removePostImage = (index: number) => {
    setPostImages(prev => prev.filter((_, i) => i !== index));
    setPostImagePreviews(prev => prev.filter((_, i) => i !== index));
  };

  // 조회수 기록 — 피드에서 글을 열면 목록 데이터로 바로 띄워 서버에 조회가 남지 않았다
  // (2026-10 월간 리포트가 "피드 조회 0" 의심 → 실측으로 확인). 상세 API 호출이 조회수를 올린다.
  // 같은 세션에서 같은 글을 다시 여는 것은 세지 않는다.
  /** 이 세션에서 이미 조회로 센 글이면 true, 아니면 기록하고 false */
  const markPostViewed = (postId: number): boolean => {
    try {
      const key = 'pulse_viewed_posts';
      const seen: number[] = JSON.parse(sessionStorage.getItem(key) || '[]');
      if (seen.includes(postId)) return true;
      sessionStorage.setItem(key, JSON.stringify([...seen, postId].slice(-500)));
    } catch {
      // 저장소를 쓸 수 없는 환경에서도 조회는 기록한다
    }
    return false;
  };

  const recordPostView = (postId: number) => {
    if (markPostViewed(postId)) return;
    postsAPI
      .getPost(postId)
      .then(() =>
        setSelectedPost((prev: any) =>
          prev && prev.id === postId ? { ...prev, views: (Number(prev.views) || 0) + 1 } : prev
        )
      )
      .catch(() => {});
  };

  // 상세 모달이 열린 동안: 배경 피드 스크롤 잠금(배경이 같이 움직이며 뒤에서 무한스크롤이 돌았다), Esc로 닫기
  useEffect(() => {
    if (!showPostDetailModal) return;
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        setShowPostDetailModal(false);
        setShowMoreMenu(false);
      }
    };
    document.addEventListener('keydown', onKey);
    return () => {
      document.body.style.overflow = prevOverflow;
      document.removeEventListener('keydown', onKey);
    };
  }, [showPostDetailModal]);

  // 피드·공유 링크·알림 — 어디서 열든 이 경로로 연다.
  // viewRecorded: 상세 API로 이미 불러온 경우(그 호출이 조회수를 올렸다) 다시 세지 않는다.
  const openPostDetail = (post: any, opts: { viewRecorded?: boolean } = {}) => {
    setSelectedPost(post);
    setShowPostDetailModal(true);
    setShowMoreMenu(false);
    if (post.liked) setLikedPosts(prev => new Set(prev).add(post.id));
    if (opts.viewRecorded) markPostViewed(post.id);
    else recordPostView(post.id);
    // 댓글: 불러 둔 것이 있으면 먼저 보여 주고, 항상 최신으로 다시 받는다(다른 사람의 새 댓글)
    loadComments(post.id);
  };

  const openProfile = (userId: number) => {
    setProfileUserId(userId);
    setShowProfileModal(true);
  };

  // 로고 클릭 시 홈(피드)으로 이동
  const goToHome = () => {
    setActiveTab('feed');
    setShowMobileMenu(false); // 모바일 메뉴 닫기
    window.scrollTo({ top: 0, behavior: 'smooth' }); // 페이지 맨 위로 스크롤
  };

  const copyShareLink = () => {
    if (!selectedPost?.id) return;
    
    const shareUrl = `${window.location.origin}${window.location.pathname}?postId=${selectedPost.id}`;
    
    navigator.clipboard.writeText(shareUrl).then(() => {
      alert('게시글 링크가 복사되었습니다! 📋\n링크를 공유하면 다른 사람들이 이 게시글을 바로 볼 수 있습니다.');
      setShowMoreMenu(false);
    }).catch((error) => {
      console.error('클립보드 복사 실패:', error);
      prompt('게시글 링크를 복사하세요:', shareUrl);
      setShowMoreMenu(false);
    });
  };

  // 게시글 카드에서 공유 (클릭 이벤트 전파 방지)
  const sharePostFromCard = async (postId: number, e: React.MouseEvent) => {
    e.stopPropagation(); // 카드 클릭 이벤트 방지
    
    const shareUrl = `${window.location.origin}${window.location.pathname}?postId=${postId}`;
    
    try {
      await navigator.clipboard.writeText(shareUrl);
      alert('게시글 링크가 복사되었습니다! 📋');
    } catch (error) {
      console.error('클립보드 복사 실패:', error);
      prompt('게시글 링크를 복사하세요:', shareUrl);
    }
  };

  const openEdit = (post: any) => {
    setShowPostDetailModal(false);
    setEditingPostId(post.id);
    setEditPost({
      title: post.title || '',
      content: post.content || '',
      type: post.type || 'case_study',
      tags: (post.tags || []).join(', ')
    });
    setShowEditModal(true);
  };

  const handleUpdatePost = async () => {
    if (!editingPostId) return;
    try {
      await updatePost(editingPostId, {
        title: editPost.title,
        content: editPost.content,
        type: editPost.type,
        tags: editPost.tags ? editPost.tags.split(',').map(t => t.trim()).filter(Boolean) : []
      });
      setShowEditModal(false);
      setEditingPostId(null);
    } catch (error) {
      console.error('게시글 수정 실패:', error);
    }
  };

  const handleDeletePost = async (id: number) => {
    if (!window.confirm('정말로 이 게시글을 삭제하시겠습니까?')) return;
    try {
      await deletePost(id);
      setShowPostDetailModal(false);
    } catch (error) {
      console.error('게시글 삭제 실패:', error);
    }
  };

  // /login 진입 시 로그인 모달 자동 오픈
  useEffect(() => {
    if (window.location.pathname === '/login') {
      setShowLoginModal(true);
    }
  }, []);

  // 사용 중 세션이 끝나면 읽던 화면은 그대로 두고 로그인 창만 띄운다
  const [loginNotice, setLoginNotice] = useState('');
  useEffect(() => {
    const onExpired = () => {
      setLoginNotice('로그인이 만료되었습니다. 다시 로그인해 주세요.');
      setShowLoginModal(true);
    };
    window.addEventListener(AUTH_EXPIRED_EVENT, onExpired);
    return () => window.removeEventListener(AUTH_EXPIRED_EVENT, onExpired);
  }, []);

  const closeLoginModalAndRedirect = () => {
    setShowLoginModal(false);
    if (window.location.pathname === '/login') {
      window.history.replaceState({}, '', '/');
    }
  };

  return (
    <div className="flex bg-white min-h-screen">
      {window.location.pathname === '/admin' ? (
        <div className="flex-1">
          {isAuthenticated ? (
            <AdminErrorBoundary><AdminDashboard /></AdminErrorBoundary>
          ) : (
            <div className="p-8">
              <h2 className="text-xl font-semibold mb-2">접근 권한이 필요합니다</h2>
              <p className="text-gray-600">관리자 전용 페이지입니다. 로그인 후 이용하세요.</p>
            </div>
          )}
        </div>
      ) : (<>
      {/* Mobile Menu Overlay */}
      {showMobileMenu && (
        <div 
          className="fixed inset-0 bg-black bg-opacity-50 z-40 lg:hidden"
          onClick={() => setShowMobileMenu(false)}
        />
      )}
      
      {/* Left Sidebar */}
      <div className={`w-80 bg-white border-r border-gray-200 h-screen overflow-y-auto fixed left-0 top-0 z-50 transform transition-transform duration-300 ease-in-out lg:translate-x-0 ${showMobileMenu ? 'translate-x-0' : '-translate-x-full'} lg:block`}>
        <div className="p-6">
          {/* Logo */}
          <button
            onClick={goToHome}
            className="flex items-center mb-8 w-full hover:bg-gray-50 p-2 rounded-lg transition-colors cursor-pointer active:bg-gray-100"
          >
            <Activity className="w-8 h-8 text-blue-600 mr-2" />
            <div className="text-left">
              <h1 className="text-2xl font-bold text-gray-900">Pulse</h1>
              <p className="text-xs text-gray-500">의료진 전용 플랫폼</p>
            </div>
          </button>

          {/* Profile Section */}
          {isAuthenticated && user ? (
            <div 
              className="border border-gray-200 rounded-xl p-4 mb-6 cursor-pointer hover:bg-gray-50 transition-colors"
              onClick={() => {
                if (user?.id) {
                  openProfile(user.id);
                  setShowMobileMenu(false);
                }
              }}
            >
              <div className="flex items-center mb-3">
                <div className="w-9 h-9 rounded-full bg-gray-100 text-gray-500 flex items-center justify-center text-sm font-medium mr-3 flex-shrink-0">
                  {(user.name || '?').charAt(0)}
                </div>
                <div>
                  <h3 className="font-semibold text-gray-900">{user.name}</h3>
                  <p className="text-sm text-gray-600">{user.specialization || '의료진'}</p>
                  {user.isVerifiedDoctor && (
                    <div className="flex items-center mt-1">
                      <Shield className="w-3 h-3 text-green-500 mr-1" />
                      <span className="text-xs text-green-600">검증됨</span>
                    </div>
                  )}
                </div>
              </div>
              <div className="text-xs text-gray-500 text-center">클릭하여 프로필 보기</div>
            </div>
          ) : (
            <GuestMode
              onLogin={() => setShowLoginModal(true)}
              onSignup={() => setShowSignupModal(true)}
            />
          )}

          {/* Navigation */}
          <div className="space-y-2">
            <button
              onClick={() => setActiveTab('feed')}
              className={`w-full flex items-center px-3 py-2 rounded-lg text-left transition-colors ${
                activeTab === 'feed' ? 'bg-gray-100 text-gray-900' : 'text-gray-700 hover:bg-gray-100'
              }`}
            >
              <Activity className="w-5 h-5 mr-3" />
              전체피드
            </button>
            <button
              onClick={() => setActiveTab('cases')}
              className={`w-full flex items-center px-3 py-2 rounded-lg text-left transition-colors ${
                activeTab === 'cases' ? 'bg-gray-100 text-gray-900' : 'text-gray-700 hover:bg-gray-100'
              }`}
            >
              <Stethoscope className="w-5 h-5 mr-3" />
              사례공유
            </button>
            <button
              onClick={() => setActiveTab('research')}
              className={`w-full flex items-center px-3 py-2 rounded-lg text-left transition-colors ${
                activeTab === 'research' ? 'bg-gray-100 text-gray-900' : 'text-gray-700 hover:bg-gray-100'
              }`}
            >
              <FlaskConical className="w-5 h-5 mr-3" />
              연구동향
            </button>
            <button
              onClick={() => setActiveTab('discussion')}
              className={`w-full flex items-center px-3 py-2 rounded-lg text-left transition-colors ${
                activeTab === 'discussion' ? 'bg-gray-100 text-gray-900' : 'text-gray-700 hover:bg-gray-100'
              }`}
            >
              <MessageCircle className="w-5 h-5 mr-3" />
              토론하기
            </button>
            <button
              onClick={() => setActiveTab('jobs')}
              className={`w-full flex items-center px-3 py-2 rounded-lg text-left transition-colors ${
                activeTab === 'jobs' ? 'bg-gray-100 text-gray-900' : 'text-gray-700 hover:bg-gray-100'
              }`}
            >
              <Briefcase className="w-5 h-5 mr-3" />
              채용정보
            </button>
            <button
              onClick={() => setActiveTab('medical_dispute')}
              className={`w-full flex items-center px-3 py-2 rounded-lg text-left transition-colors ${
                activeTab === 'medical_dispute' ? 'bg-gray-100 text-gray-900' : 'text-gray-700 hover:bg-gray-100'
              }`}
            >
              <Shield className="w-5 h-5 mr-3" />
              의료분쟁
            </button>

            {/* 채널 (슬랙식 개인/공유 워크스페이스) */}
            {isAuthenticated && (
              <ChannelsNav
                activeTab={activeTab}
                onSelectChannel={(id) => { setActiveTab(`channel:${id}`); setShowMobileMenu(false); }}
                refreshKey={channelRefresh}
                onChanged={() => setChannelRefresh((x) => x + 1)}
              />
            )}

            {/* 스폰서 센터 (광고주 셀프서비스) */}
            {isAuthenticated && (
              <button
                onClick={() => setActiveTab('sponsor-portal')}
                className={`w-full flex items-center px-3 py-2 rounded-lg text-left transition-colors ${
                  activeTab === 'sponsor-portal' ? 'bg-gray-100 text-gray-900' : 'text-gray-700 hover:bg-gray-100'
                }`}
              >
                <TrendingUp className="w-5 h-5 mr-3" />
                스폰서 센터
              </button>
            )}

            {/* 개발자 메뉴 */}
            <div className="border-t border-gray-200 my-2"></div>
            <button
              onClick={() => setActiveTab('api-reference')}
              className={`w-full flex items-center px-3 py-2 rounded-lg text-left transition-colors ${
                activeTab === 'api-reference' ? 'bg-gray-100 text-gray-900' : 'text-gray-700 hover:bg-gray-100'
              }`}
            >
              <BookOpen className="w-5 h-5 mr-3" />
              API 문서
            </button>

            {/* 관리자 메뉴 */}
            {isAuthenticated && user && ['admin@pulse.com', 'admin.test@pulse.com'].includes(user.email) && (
              <>
                <button
                  onClick={() => setActiveTab('admin')}
                  className={`w-full flex items-center px-3 py-2 rounded-lg text-left transition-colors ${
                    activeTab === 'admin' ? 'bg-gray-100 text-gray-900' : 'text-gray-700 hover:bg-gray-100'
                  }`}
                >
                  <Settings className="w-5 h-5 mr-3" />
                  관리자 대시보드
                </button>
              </>
            )}
          </div>

          {/* Logout Button */}
          {isAuthenticated && (
            <div className="mt-8">
              <button
                onClick={logout}
                className="w-full flex items-center px-3 py-2 rounded-lg text-left text-red-600 hover:bg-red-50 transition-colors"
              >
                <User className="w-5 h-5 mr-3" />
                로그아웃
              </button>
            </div>
          )}
        {/* 푸터 — 정책 링크와 사업자 정보. 처리방침은 다른 링크와 구분되게 굵게(작성지침).
            이용약관은 페이지가 없어 링크를 두지 않는다(만들면 다시 추가). */}
        <footer className="mt-12 pt-6 border-t border-gray-100 text-xs text-gray-500">
          <ul className="flex flex-wrap gap-x-3 gap-y-1 mb-3">
            <li><a href="/privacy.html" className="font-semibold text-gray-700 hover:underline">개인정보 처리방침</a></li>
            <li><a href="https://victorschool.my.canva.site/bluewhale-ai" target="_blank" rel="noopener noreferrer" className="hover:underline hover:text-gray-700">회사소개</a></li>
            <li><a href="/docs" className="hover:underline hover:text-gray-700">API 문서</a></li>
          </ul>
          <address className="not-italic space-y-0.5 leading-relaxed">
            <div>연주미디어 · 대표 김민용</div>
            <div>사업자등록번호 459-94-01364</div>
            <div>제주특별자치도 서귀포시 대정읍 에듀시티로 148 128/403</div>
            <div>
              문의 <a href="mailto:blueslap@naver.com" className="hover:underline hover:text-gray-700">blueslap@naver.com</a> · 010-8158-1449
            </div>
          </address>
          <div className="mt-3">© {new Date().getFullYear()} Pulse</div>
        </footer>
                </div>
              </div>

      {/* Main Content */}
      <div className="flex-1 lg:ml-80 xl:mr-80">
        {/* Guest Mode Banner - 제거됨 */}

        {/* Header */}
        <div className="bg-white border-b border-gray-200 px-4 lg:px-6 py-4 sticky top-0 z-30">
          <div className="flex items-center justify-between gap-4">
            {/* Mobile Menu Button */}
                <button
              onClick={() => setShowMobileMenu(true)}
              className="lg:hidden p-2 text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded-lg transition-colors"
                >
              <Menu className="w-6 h-6" />
                </button>
            
            <div className="flex-1 max-w-2xl">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
                <input
                  type="text"
                  placeholder="의료 정보 검색..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-gray-400"
                />
              </div>
            </div>
            <div className="flex items-center space-x-2 lg:space-x-4">
              {/* 채널 뷰에서는 전역 피드 글쓰기 버튼을 숨긴다(채널은 자체 작성 UI 사용) */}
              {!activeTab.startsWith('channel:') && (
                <button
                  onClick={() => setShowPostModal(true)}
                  disabled={!isAuthenticated}
                  className="flex items-center px-3 py-2 lg:px-4 bg-gray-900 text-white rounded-lg hover:bg-gray-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors text-sm lg:text-base min-h-[44px]"
                  title={!isAuthenticated ? "로그인이 필요합니다" : "게시글 작성"}
                >
                  <Plus className="w-4 h-4 lg:mr-2" />
                  <span className="hidden sm:inline">글쓰기</span>
                </button>
              )}
              {/* Notification Bell with Badge */}
              <div className="relative notification-dropdown-container">
                <button 
                  onClick={toggleNotifications} 
                  className="p-2 lg:p-3 text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded-lg transition-colors min-h-[44px] min-w-[44px] relative"
                  disabled={!isAuthenticated}
                  title={!isAuthenticated ? "로그인이 필요합니다" : "알림"}
                >
                  <Bell className="w-5 h-5 lg:w-6 lg:h-6" />
                  {isAuthenticated && unreadCount > 0 && (
                    <span className="absolute top-1 right-1 bg-red-500 text-white text-xs font-bold rounded-full w-5 h-5 flex items-center justify-center">
                      {unreadCount > 9 ? '9+' : unreadCount}
                    </span>
                  )}
              </button>

                {/* Notification Dropdown */}
                {notificationsOpen && isAuthenticated && (
                  <div className="absolute right-0 mt-2 w-80 lg:w-96 bg-white rounded-lg shadow-xl border border-gray-200 z-50 max-h-96 overflow-y-auto">
                    <div className="p-4 border-b border-gray-200 bg-gray-50">
                      <div className="flex items-center justify-between">
                        <h3 className="font-semibold text-gray-900">알림</h3>
                        {unreadCount > 0 && (
                          <span className="text-xs text-gray-900 font-medium">
                            {unreadCount}개의 새 알림
                          </span>
                        )}
                      </div>
                    </div>
                    <div className="divide-y divide-gray-100">
                      {notifications.length === 0 ? (
                        <div className="p-8 text-center text-gray-500">
                          <Bell className="w-12 h-12 mx-auto mb-3 text-gray-300" />
                          <p className="text-sm">새로운 알림이 없습니다</p>
                        </div>
                      ) : (
                        notifications.map((notification) => (
                          <div
                            key={notification.id}
                            onClick={() => handleNotificationClick(notification)}
                            className={`p-4 cursor-pointer hover:bg-gray-50 transition-colors ${
                              !notification.isRead ? 'bg-gray-50' : ''
                            }`}
                          >
                            <div className="flex items-start">
                              <div className="flex-shrink-0 mr-3">
                                {notification.type === 'like' && (
                                  <div className="w-10 h-10 bg-gray-100 rounded-full flex items-center justify-center">
                                    <Heart className="w-5 h-5 text-red-600" />
                                  </div>
                                )}
                                {notification.type === 'comment' && (
                                  <div className="w-10 h-10 bg-gray-100 rounded-full flex items-center justify-center">
                                    <MessageCircle className="w-5 h-5 text-gray-500" />
                                  </div>
                                )}
                                {notification.type === 'follow' && (
                                  <div className="w-10 h-10 bg-gray-100 rounded-full flex items-center justify-center">
                                    <User className="w-5 h-5 text-green-600" />
                                  </div>
                                )}
                              </div>
                              <div className="flex-1 min-w-0">
                                <p className="text-sm text-gray-900 mb-1">
                                  {notification.message}
                                </p>
                                <p className="text-xs text-gray-500">
                                  {formatRelativeTime(notification.createdAt)}
                                </p>
                              </div>
                              {!notification.isRead && (
                                <div className="flex-shrink-0 ml-2">
                                  <div className="w-2 h-2 bg-gray-900 rounded-full"></div>
                                </div>
                              )}
                            </div>
                          </div>
                        ))
                      )}
                    </div>
                  </div>
                )}
              </div>
              {/* Mobile/Desktop AI Button */}
              <button
                onClick={() => setShowAiModal(true)}
                className="p-2 lg:p-3 text-gray-500 hover:text-gray-900 hover:bg-gray-100 rounded-lg transition-colors min-h-[44px] min-w-[44px]"
                title="AI 상담"
              >
                <Brain className="w-5 h-5 lg:w-6 lg:h-6" />
              </button>
            </div>
          </div>
        </div>

        {/* Content */}
        <div className="p-4 lg:p-6">
          {activeTab === 'sponsor-portal' ? (
            <SponsorPortal />
          ) : activeTab.startsWith('channel:') ? (
            // 채널 뷰 (슬랙식 워크스페이스)
            <ChannelView
              channelId={Number(activeTab.slice(8))}
              currentUserId={user?.id}
              onChannelDeleted={() => setActiveTab('feed')}
              onChanged={() => setChannelRefresh((x) => x + 1)}
            />
          ) : activeTab === 'api-reference' ? (
            // API 문서 페이지
            <ApiDocsPage />
          ) : activeTab === 'admin' ? (
            // 관리자 대시보드
            isAuthenticated ? (
              <AdminErrorBoundary><AdminDashboard /></AdminErrorBoundary>
            ) : (
              <div className="text-center py-12">
                <div className="text-gray-500 mb-4">로그인이 필요합니다.</div>
              </div>
            )
          ) : posts.length === 0 && postsLoading ? (
            <div className="flex justify-center items-center py-12">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
              <span className="ml-3 text-gray-600">게시글을 불러오는 중...</span>
            </div>
          ) : posts.length === 0 && !postsLoading && postsError ? (
            <div className="text-center py-12">
              <div className="text-gray-500 mb-4">게시글을 불러오지 못했습니다.</div>
              <button onClick={retryPosts} className="px-4 py-2 border border-gray-300 rounded-lg text-sm text-gray-700 hover:bg-gray-50 min-h-[44px]">
                다시 시도
              </button>
            </div>
          ) : posts.length === 0 && !postsLoading ? (
            <div className="text-center py-12">
              <div className="text-gray-500 mb-4">게시글이 없습니다.</div>
              <div className="text-sm text-gray-400">다른 검색어를 시도해보세요.</div>
            </div>
          ) : (
            <div className="space-y-6">
              {posts.map((post, index) => {
                const isLastElement = posts.length === index + 1;
                // 학회 계정 글 → 학회공지 스타일 (잉크 톤: 왼쪽 굵은 보더 + Landmark 아이콘 + 아웃라인 배지)
                const isSocietyPost = ((post as any).author_account_type ?? (post as any).authorAccountType) === 'society';
                return (
                <React.Fragment key={post.id}>
                <div
                  ref={isLastElement ? lastPostElementRef : null}
                  className={`bg-white rounded-xl border border-gray-200 p-4 lg:p-6 cursor-pointer hover:border-gray-300 transition-colors active:bg-gray-50 ${isSocietyPost ? 'border-l-2 border-l-gray-900' : ''}`}
                  onClick={() => openPostDetail(post)}
                >
                  <div className="flex items-start justify-between mb-4">
                    <div
                      className="flex items-center cursor-pointer hover:bg-gray-50 rounded-lg p-2 -ml-2 transition-colors"
                      onClick={(e) => {
                        e.stopPropagation();
                        openProfile(post.authorId);
                      }}
                    >
                      {isSocietyPost ? (
                        <div className="w-9 h-9 rounded-lg bg-gray-900 text-white flex items-center justify-center mr-3 flex-shrink-0">
                          <Landmark className="w-4 h-4" />
                        </div>
                      ) : (
                      <div className="w-9 h-9 rounded-full bg-gray-100 text-gray-500 flex items-center justify-center text-sm font-medium mr-3 flex-shrink-0">
                        {(post.authorName || (post as any).author_name || '?').charAt(0)}
                      </div>
                      )}
                      <div>
                        <div className="flex items-center gap-2">
                          <h3 className="font-semibold text-gray-900 hover:underline">{post.authorName}</h3>
                          {isSocietyPost ? (
                            <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs text-gray-900 border border-gray-900 bg-white font-medium">
                              학회 공식
                            </span>
                          ) : (
                            <>
                              {post.isVerifiedDoctor && (
                                <Shield className="w-4 h-4 text-green-500" />
                              )}
                              {post.licenseVerified && (
                                <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs text-gray-500 border border-gray-200 bg-white">
                                  <Shield className="w-3 h-3 mr-1" />
                                  면허
                                </span>
                              )}
                            </>
                          )}
                        </div>
                        <p className="text-sm text-gray-600">{isSocietyPost ? '학회 공식 계정' : `${post.specialization} • ${post.hospital}`}</p>
                      </div>
                    </div>
                    <div className="flex items-center space-x-2">
                      <span className="px-2.5 py-1 rounded-full text-xs text-gray-500 border border-gray-200 bg-white">
                        {post.type === 'case_study' ? '사례' :
                         post.type === 'research' ? '연구' :
                         post.type === 'discussion' ? '토론' :
                         post.type === 'medical_dispute' ? '의료분쟁' : '채용'}
                      </span>
                      <span className="text-xs text-gray-500">{formatRelativeTime(post.createdAt)}</span>
                    </div>
                  </div>

                  {(() => {
                    // 링크 카드가 보여 주는 링크 줄과 "링크뿐인 제목"은 카드에 맡긴다(같은 URL이 세 번 보이지 않게)
                    const plain = decodeEntities(post.content);
                    const cardUrls = extractUrls(plain).slice(0, 1);
                    const excerpt = withoutUrlLines(
                      looksLikeMarkdown(post.content) ? toPlainExcerpt(post.content) : plain,
                      cardUrls
                    );
                    return (
                      <>
                        {!isUrlOnlyText(post.title) && (
                          <h2 className="text-base sm:text-lg font-semibold text-gray-900 mb-3">{post.title}</h2>
                        )}
                        {excerpt && (
                          <div className="text-sm sm:text-base text-gray-700 mb-4 leading-relaxed line-clamp-3">
                            {/* 카드 미리보기는 기호 없는 평문 발췌 — 제목·인용 서식이 3줄 안에서 튀지 않게 */}
                            {linkifyWithLineBreaks(excerpt)}
                          </div>
                        )}
                        {/* line-clamp 밖에 둔다 — 안에 두면 브라우저마다 카드가 잘리거나 사라졌다 */}
                        <LinkPreviewList content={plain} max={1} compact className="mb-4" />
                      </>
                    );
                  })()}

                  {/* Post Images */}
                  {post.images && post.images.length > 0 && (
                    <div className={`mb-4 grid gap-2 ${
                      post.images.length === 1 ? 'grid-cols-1' :
                      post.images.length === 2 ? 'grid-cols-2' :
                      'grid-cols-2 sm:grid-cols-3'
                    }`}>
                      {post.images.slice(0, 3).map((imageUrl, index) => (
                        <div 
                          key={index} 
                          // 한 장일 때도 높이를 고정한다 — 원본 비율로 늘어나면 이미지가 늦게 도착할 때 아래 카드들이 밀렸다
                          className={`relative flex items-center justify-center bg-gray-50 rounded-lg border border-gray-200 overflow-hidden cursor-pointer hover:opacity-90 transition-opacity ${post.images?.length === 1 ? 'h-64 sm:h-80' : 'h-48 sm:h-56 md:h-64'}`}
                          onClick={() => openPostDetail(post)}
                        >
                          <img
                            src={imageUrl}
                            alt={`${post.title} - 이미지 ${index + 1}`}
                            loading="lazy"
                            decoding="async"
                            className={`w-full h-full ${post.images?.length === 1 ? 'object-contain' : 'object-cover'}`}
                          />
                          {index === 2 && post.images && post.images.length > 3 && (
                            <div className="absolute inset-0 bg-black bg-opacity-50 flex items-center justify-center text-white font-semibold text-lg sm:text-xl">
                              +{post.images.length - 3}
                        </div>
                      )}
                    </div>
                      ))}
                  </div>
                  )}

                  {post.tags.length > 0 && (
                    <div className="flex flex-wrap gap-2 mb-4">
                      {post.tags.map((tag, index) => (
                        <span key={index} className="px-2 py-1 bg-gray-100 text-gray-700 rounded-md text-sm">
                          #{tag}
                        </span>
                      ))}
                    </div>
                  )}

                  <div className="flex items-center justify-between">
                    <div className="flex items-center space-x-6">
                      {/* 좋아요 버튼 - 피드에서 바로 동작 */}
                        <button
                        onClick={(e) => {
                          e.stopPropagation();
                          if (!isAuthenticated) {
                            alert('좋아요는 로그인 후 이용할 수 있습니다.');
                            return;
                          }
                          handleLike(post.id);
                        }}
                        disabled={!isAuthenticated}
                        className={`flex items-center space-x-1 transition-colors p-2 rounded-lg min-h-[44px] -ml-2 ${
                          likedPosts.has(post.id)
                            ? 'text-red-500 hover:text-red-600 active:bg-red-50'
                            : 'text-gray-500 hover:text-red-500 active:bg-gray-100'
                        } ${!isAuthenticated ? 'cursor-not-allowed opacity-50' : 'cursor-pointer'}`}
                      >
                        <Heart className={`w-5 h-5 lg:w-6 lg:h-6 ${likedPosts.has(post.id) ? 'fill-current' : ''}`} />
                        <span className="text-sm lg:text-base font-medium">{post.likes}</span>
                        </button>
                      
                      {/* 댓글 버튼 - 모달 열기 */}
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          openPostDetail(post);
                        }}
                        className="flex items-center space-x-1 text-gray-400 hover:text-gray-900 transition-colors cursor-pointer p-2 rounded-lg min-h-[44px]"
                      >
                        <MessageCircle className="w-5 h-5 lg:w-6 lg:h-6" />
                        <span className="text-sm lg:text-base">댓글</span>
                      </button>
                      
                      {/* 공유 버튼 */}
                      <button
                        onClick={(e) => sharePostFromCard(post.id, e)}
                        className="flex items-center space-x-1 text-gray-500 hover:text-green-500 active:bg-green-50 transition-colors cursor-pointer p-2 rounded-lg min-h-[44px]"
                        title="게시글 링크 복사"
                      >
                        <Share2 className="w-5 h-5 lg:w-6 lg:h-6" />
                        <span className="text-sm lg:text-base">공유</span>
                      </button>
                    </div>
                    <div className="flex items-center space-x-1 text-gray-500">
                      <Eye className="w-4 h-4" />
                      <span className="text-sm">{post.views}</span>
                    </div>
                  </div>
                </div>
                {/* 스폰서 카드 — 3번째 글 뒤 1개, 라벨 필수 (광고-콘텐츠 분리: 피드 데이터와 무관) */}
                {index === 2 && feedAd && (
                  <SponsoredCard ad={feedAd} specialty={user?.specialization} />
                )}
                </React.Fragment>
              )})}
            </div>
          )}
          {postsLoading && posts.length > 0 && (
            <div className="flex justify-center items-center py-6">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
              <span className="ml-3 text-gray-600">더 많은 게시글을 불러오는 중...</span>
            </div>
          )}
          {!postsLoading && postsError && posts.length > 0 && (
            <div className="text-center py-6">
              <p className="text-gray-500 mb-3">게시글을 더 불러오지 못했습니다.</p>
              <button onClick={retryPosts} className="px-4 py-2 border border-gray-300 rounded-lg text-sm text-gray-700 hover:bg-gray-50 min-h-[44px]">
                다시 시도
              </button>
            </div>
          )}
          {!postsLoading && !postsError && !hasMore && posts.length > 0 && (
            <div className="text-center py-6">
              <p className="text-gray-500">모든 게시글을 불러왔습니다.</p>
            </div>
          )}
        </div>
      </div>

      {/* Mobile Right Sidebar Overlay */}
      {showMobileRightBar && (
        <div 
          className="fixed inset-0 bg-black bg-opacity-50 z-40 xl:hidden"
          onClick={() => setShowMobileRightBar(false)}
        />
      )}

      {/* Right Sidebar */}
      <div className={`w-80 bg-white border-l border-gray-200 h-screen overflow-y-auto fixed right-0 top-0 z-50 transform transition-transform duration-300 ease-in-out ${showMobileRightBar ? 'translate-x-0 xl:hidden' : 'translate-x-full xl:translate-x-0'} xl:block`}>
        <div className="p-6">
          {/* Mobile Close Button */}
          {showMobileRightBar && (
            <button
              onClick={() => setShowMobileRightBar(false)}
              className="xl:hidden absolute top-4 right-4 p-2 text-gray-400 hover:text-gray-600"
            >
              <X className="w-6 h-6" />
            </button>
          )}
          
          <div className="mb-6 sticky top-0 bg-white z-10">
            <h3 className="font-semibold text-gray-900 mb-3 flex items-center">
              <Brain className="w-4 h-4 mr-2 text-gray-400" />
              AI 상담
            </h3>
            {/* 게스트 모드 안내 - 제거됨 */}
            
            {/* AI 상담 시작 버튼 */}
              <button
              onClick={() => setShowAiModal(true)}
              className="w-full px-4 py-3 bg-gray-900 text-white rounded-xl hover:bg-gray-700 transition-colors font-medium flex items-center justify-center"
              >
              AI 상담 시작
              </button>
            
            <div className="mt-4 space-y-2">
              <p className="text-xs text-gray-600 text-center">
                AI 기반 의료 정보 상담 서비스
              </p>
              <div className="grid grid-cols-2 gap-2">
                {suggestedPrompts.map((p, idx) => (
                  <button
                    key={idx}
                    onClick={() => {
                      setAiQuery(p);
                      setShowAiModal(true);
                    }}
                    className="text-left px-2 py-2 text-xs bg-gray-50 hover:bg-gray-100 border border-gray-200 rounded"
                  >
                    {p}
                  </button>
                ))}
            </div>
                    </div>
          </div>

          {/* 유료 서베이·자문단 (검증 의사 전용) */}
          {isAuthenticated && user?.isVerifiedDoctor && (
            <SurveyPanel specialty={user?.specialization} />
          )}

          <div className="mb-6">
            <h3 className="font-semibold text-gray-900 mb-3 flex items-center justify-between">
              <span className="flex items-center"><TrendingUp className="w-4 h-4 mr-2" />
              트렌딩 토픽</span>
              <button
                onClick={() => setTrendingOpen(v => !v)}
                aria-label="트렌딩 토글"
                className="p-1 text-gray-500 hover:text-gray-800"
              >
                {trendingOpen ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
              </button>
            </h3>
            {trendingOpen && (
              <div className="space-y-2">
                {trendingTopics.map((topic, index) => (
                  <div key={index} className="flex justify-between items-center py-2 px-3 bg-gray-50 rounded-lg hover:bg-gray-100 cursor-pointer transition-colors">
                    <div className="flex items-center">
                      <span className="text-sm font-medium text-gray-700">#{topic.tag}</span>
                      {topic.trend === 'up' && <TrendingUp className="w-3 h-3 text-gray-400 ml-1" />}
                    </div>
                    <span className="text-xs text-gray-500">{topic.count}</span>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Post Modal */}
      {showPostModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-2xl mx-4 max-h-[80vh] overflow-y-auto">
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-xl font-bold text-gray-900">새 게시글 작성</h2>
              <button
                onClick={() => setShowPostModal(false)}
                className="text-gray-400 hover:text-gray-600"
              >
                <X className="w-6 h-6" />
              </button>
            </div>

            <div className="space-y-4">
              <div className="flex gap-2">
                <button
                  type="button"
                  onClick={() => setCaseMode(false)}
                  className={`px-3 py-1.5 text-sm rounded-md border transition-colors ${!caseMode ? 'bg-gray-900 text-white border-gray-900' : 'border-gray-300 text-gray-600 hover:border-gray-500'}`}
                >
                  일반 글
                </button>
                <button
                  type="button"
                  onClick={() => setCaseMode(true)}
                  className={`px-3 py-1.5 text-sm rounded-md border transition-colors ${caseMode ? 'bg-gray-900 text-white border-gray-900' : 'border-gray-300 text-gray-600 hover:border-gray-500'}`}
                >
                  증례 공유
                </button>
              </div>

              {!caseMode && (
                <div className="border border-gray-200 p-4 rounded-lg">
                  <p className="text-sm text-gray-600">
                    <strong className="text-gray-900">스마트 포스팅</strong> — 내용이나 링크, 사진만 남기면 제목·태그·카테고리는 자동으로 분류됩니다.
                  </p>
                </div>
              )}

              {caseMode && (
                <CaseGuide
                  content={newPost.content}
                  onInsertTemplate={() =>
                    setNewPost((prev) => ({
                      ...prev,
                      content: prev.content.trim() ? `${prev.content.trim()}\n\n${CASE_TEMPLATE}` : CASE_TEMPLATE,
                    }))
                  }
                  consent={caseConsent}
                  deidentified={caseDeidentified}
                  onConsentChange={setCaseConsent}
                  onDeidentifiedChange={setCaseDeidentified}
                />
              )}

              {postError && (
                <div className="border border-red-300 bg-red-50 text-red-700 text-sm rounded-md p-3 whitespace-pre-line">{postError}</div>
              )}

              <div>
                <textarea
                  value={newPost.content}
                  onChange={(e) => setNewPost({ ...newPost, content: e.target.value })}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-gray-400 min-h-[200px] text-base resize-y"
                  placeholder="공유할 케이스, 최근 읽은 논문 링크, 질문 등을 자유롭게 적어주세요..."
                  autoFocus
                />
              </div>

              {/* Advanced Options Toggle */}
              <details className="group">
                <summary className="text-sm text-gray-500 cursor-pointer hover:text-gray-700 font-medium mb-2">
                  직접 제목/태그 입력하기 (선택사항)
                </summary>
                <div className="mt-3 space-y-4 pl-2 border-l-2 border-gray-200">
                  <div>
                    <label className="block text-xs font-medium text-gray-700 mb-1">유형</label>
                    <select
                      value={newPost.type}
                      onChange={(e) => setNewPost({ ...newPost, type: e.target.value })}
                      className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-gray-400"
                    >
                      <option value="case_study">사례공유</option>
                      <option value="research">연구동향</option>
                      <option value="discussion">토론하기</option>
                      <option value="job">채용정보</option>
                      <option value="medical_dispute">의료분쟁</option>
                    </select>
                  </div>

                  <div>
                    <label className="block text-xs font-medium text-gray-700 mb-1">제목</label>
                    <input
                      type="text"
                      value={newPost.title}
                      onChange={(e) => setNewPost({ ...newPost, title: e.target.value })}
                      className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-gray-400"
                      placeholder="AI가 자동으로 생성합니다"
                    />
                  </div>

                  <div>
                    <label className="block text-xs font-medium text-gray-700 mb-1">태그 (쉼표로 구분)</label>
                    <input
                      type="text"
                      value={newPost.tags}
                      onChange={(e) => setNewPost({ ...newPost, tags: e.target.value })}
                      className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-gray-400"
                      placeholder="AI가 자동으로 추출합니다"
                    />
                  </div>
                </div>
              </details>

              {/* Image Upload */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">이미지 (선택, 최대 5개)</label>
                <div className="space-y-3">
                  <label className="flex items-center justify-center w-full px-4 py-3 border border-dashed border-gray-300 rounded-lg cursor-pointer hover:border-gray-500 hover:bg-gray-50 transition-colors">
                    <input
                      type="file"
                      accept="image/*"
                      multiple
                      onChange={handlePostImageUpload}
                      className="hidden"
                      disabled={postSubmitting}
                    />
                    <div className="text-center">
                      <svg className="mx-auto h-8 w-8 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                      </svg>
                      <span className="text-sm text-gray-600">이미지 선택 (각 10MB 이하)</span>
                    </div>
                  </label>
                  
                  {/* Image Previews */}
                  {postImagePreviews.length > 0 && (
                    <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                      {postImagePreviews.map((preview, index) => (
                        <div key={index} className="relative group">
                          <img
                            src={preview}
                            alt={`Preview ${index + 1}`}
                            className="w-full h-32 object-cover rounded-lg border-2 border-gray-200"
                          />
                          <button
                            type="button"
                            onClick={() => removePostImage(index)}
                            className="absolute top-1 right-1 bg-red-500 text-white rounded-full p-1 opacity-0 group-hover:opacity-100 transition-opacity hover:bg-red-600"
                            disabled={postSubmitting}
                          >
                            <X className="w-4 h-4" />
                          </button>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>

              <div className="flex justify-end space-x-3">
                <button
                  onClick={() => setShowPostModal(false)}
                  disabled={postSubmitting}
                  className="px-4 py-2 border border-gray-300 text-gray-700 rounded-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  취소
                </button>
                <button
                  onClick={handleNewPost}
                  disabled={postSubmitting || !newPost.content || (caseMode && !(caseConsent && caseDeidentified))}
                  className="px-4 py-2 bg-gray-900 text-white rounded-md hover:bg-gray-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center"
                >
                  {postSubmitting ? (
                    <>
                      <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                      </svg>
                      게시 중...
                    </>
                  ) : (
                    '게시'
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Edit Modal */}
      {showEditModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-2xl mx-4 max-h-[80vh] overflow-y-auto">
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-xl font-bold text-gray-900">게시글 수정</h2>
              <button
                onClick={() => setShowEditModal(false)}
                className="text-gray-400 hover:text-gray-600"
              >
                <X className="w-6 h-6" />
              </button>
            </div>

            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">유형</label>
                <select
                  value={editPost.type}
                  onChange={(e) => setEditPost({ ...editPost, type: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-gray-400"
                >
                  <option value="case_study">사례공유</option>
                  <option value="research">연구동향</option>
                  <option value="discussion">토론하기</option>
                  <option value="job">채용정보</option>
                  <option value="medical_dispute">의료분쟁</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">제목</label>
                <input
                  type="text"
                  value={editPost.title}
                  onChange={(e) => setEditPost({ ...editPost, title: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-gray-400"
                  placeholder="제목을 입력하세요"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">내용</label>
                <textarea
                  value={editPost.content}
                  onChange={(e) => setEditPost({ ...editPost, content: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-gray-400 h-32"
                  placeholder="내용을 입력하세요"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">태그 (쉼표로 구분)</label>
                <input
                  type="text"
                  value={editPost.tags}
                  onChange={(e) => setEditPost({ ...editPost, tags: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-gray-400"
                  placeholder="예: 심근경색, 응급의학, PCI"
                />
              </div>

              <div className="flex justify-end space-x-3">
                <button
                  onClick={() => setShowEditModal(false)}
                  className="px-4 py-2 border border-gray-300 text-gray-700 rounded-md hover:bg-gray-50"
                >
                  취소
                </button>
                <button
                  onClick={handleUpdatePost}
                  className="px-4 py-2 bg-gray-900 text-white rounded-md hover:bg-gray-700"
                >
                  저장
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Post Detail Modal */}
      {showPostDetailModal && selectedPost && (
        <div 
          className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-2 sm:p-4"
          onClick={() => setShowMoreMenu(false)}
        >
          <div 
            className="bg-white rounded-lg w-full max-w-3xl max-h-[95vh] sm:max-h-[90vh] overflow-y-auto overscroll-contain"
            role="dialog"
            aria-modal="true"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Header */}
            <div className="sticky top-0 bg-white border-b border-gray-200 p-4 sm:p-6 flex justify-between items-start z-10">
              <div className="flex-1">
                <div 
                  className="flex items-center mb-2 cursor-pointer hover:bg-gray-50 rounded-lg p-2 -ml-2 transition-colors"
                  onClick={(e) => {
                    e.stopPropagation();
                    openProfile((selectedPost as any).authorId);
                  }}
                >
                  {(((selectedPost as any).author_account_type ?? (selectedPost as any).authorAccountType) === 'society') ? (
                    <div className="w-9 h-9 rounded-lg bg-gray-900 text-white flex items-center justify-center mr-3 flex-shrink-0">
                      <Landmark className="w-4 h-4" />
                    </div>
                  ) : (
                  <div className="w-9 h-9 rounded-full bg-gray-100 text-gray-500 flex items-center justify-center text-sm font-medium mr-3 flex-shrink-0">
                    {(selectedPost.authorName || (selectedPost as any).author_name || '?').charAt(0)}
                  </div>
                  )}
                  <div>
                    <div className="flex items-center gap-2">
                      <h3 className="font-semibold text-gray-900 hover:underline">{selectedPost.authorName}</h3>
                      {(((selectedPost as any).author_account_type ?? (selectedPost as any).authorAccountType) === 'society') ? (
                        <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs text-gray-900 border border-gray-900 bg-white font-medium">
                          학회 공식
                        </span>
                      ) : (
                        selectedPost.isVerifiedDoctor && (
                          <Shield className="w-4 h-4 text-green-500" />
                        )
                      )}
                    </div>
                    <p className="text-sm text-gray-600">
                      {(((selectedPost as any).author_account_type ?? (selectedPost as any).authorAccountType) === 'society')
                        ? '학회 공식 계정'
                        : `${selectedPost.specialization} • ${selectedPost.hospital}`}
                    </p>
                  </div>
                </div>
                <div className="flex items-center space-x-2">
                  <span className="px-2.5 py-1 rounded-full text-xs text-gray-500 border border-gray-200 bg-white">
                    {selectedPost.type === 'case_study' ? '사례' :
                     selectedPost.type === 'research' ? '연구' :
                     selectedPost.type === 'discussion' ? '토론' :
                     selectedPost.type === 'medical_dispute' ? '의료분쟁' : '채용'}
                  </span>
                  <span className="text-xs text-gray-500" title={selectedPost.createdAt}>
                    {formatRelativeTime(selectedPost.createdAt)}
                  </span>
                </div>
              </div>
              <div className="flex items-center space-x-2 ml-4">
                {/* More Menu */}
                <div className="relative">
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      setShowMoreMenu(!showMoreMenu);
                    }}
                    className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-full transition-colors"
                  >
                    <MoreVertical className="w-5 h-5" />
                  </button>
                  
                  {/* Dropdown Menu */}
                  {showMoreMenu && (
                    <div className="absolute right-0 mt-2 w-48 bg-white rounded-lg shadow-lg border border-gray-200 py-1 z-50">
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          copyShareLink();
                        }}
                        className="w-full px-4 py-2 text-left text-sm text-gray-700 hover:bg-gray-100 flex items-center"
                      >
                        <Link className="w-4 h-4 mr-3" />
                        공유 링크 복사
                      </button>
                      
                      {(() => {
                        const authorId = (selectedPost as any).authorId || (selectedPost as any).author_id;
                        const isOwner = isAuthenticated && user && user.id === authorId;
                        const isAdmin = isAuthenticated && user && ['admin@pulse.com','admin.test@pulse.com'].includes(user.email);
                        console.log('[MORE MENU] Auth check:', {
                          authorId,
                          userId: user?.id,
                          isOwner,
                          isAdmin,
                          shouldShow: isOwner || isAdmin
                        });
                        return (isOwner || isAdmin) && (
                          <>
                            <div className="border-t border-gray-200 my-1"></div>
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                setShowMoreMenu(false);
                                openEdit(selectedPost);
                              }}
                              className="w-full px-4 py-2 text-left text-sm text-gray-700 hover:bg-gray-100 flex items-center"
                            >
                              <Pencil className="w-4 h-4 mr-3" />
                              수정
                            </button>
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                setShowMoreMenu(false);
                                handleDeletePost(selectedPost.id);
                              }}
                              className="w-full px-4 py-2 text-left text-sm text-red-600 hover:bg-red-50 flex items-center"
                            >
                              <Trash2 className="w-4 h-4 mr-3" />
                              삭제
                            </button>
                          </>
                        );
                      })()}
                    </div>
                  )}
                </div>
                
                <button
                  onClick={() => {
                    setShowPostDetailModal(false);
                    setShowMoreMenu(false);
                  }}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <X className="w-6 h-6" />
                </button>
              </div>
            </div>

            {/* Content */}
            <div className="p-4 sm:p-6">
              {!isUrlOnlyText(selectedPost.title) && (
                <h2 className="text-xl sm:text-2xl font-bold text-gray-900 mb-3 sm:mb-4">{selectedPost.title}</h2>
              )}
              <div className="text-sm sm:text-base text-gray-700 mb-4 sm:mb-6 leading-relaxed">
                {looksLikeMarkdown(selectedPost.content) ? (
                  <MarkdownRenderer content={selectedPost.content} />
                ) : (
                  <div className="whitespace-pre-wrap">
                    {linkifyWithLineBreaks(decodeEntities(selectedPost.content))}
                  </div>
                )}
                <LinkPreviewList content={decodeEntities(selectedPost.content)} className="mt-4" />
              </div>

              {/* Post Images in Detail Modal */}
              {selectedPost.images && selectedPost.images.length > 0 && (
                <div className="mb-4 sm:mb-6 space-y-3 sm:space-y-4">
                  {selectedPost.images.map((imageUrl: string, index: number) => (
                    <div key={index} className="w-full bg-black/5 rounded-lg border border-gray-200 overflow-hidden">
                      <img
                        src={imageUrl}
                        alt={`${selectedPost.title} - 이미지 ${index + 1}`}
                        loading="lazy"
                        className="w-full max-h-[60vh] sm:max-h-[70vh] object-contain"
                      />
                    </div>
                  ))}
                </div>
              )}

              {selectedPost.tags && selectedPost.tags.length > 0 && (
                <div className="flex flex-wrap gap-2 mb-6">
                  {selectedPost.tags.map((tag: string, index: number) => (
                    <span key={index} className="px-3 py-1 bg-gray-100 text-gray-700 rounded-md text-sm">
                      #{tag}
                    </span>
                  ))}
                </div>
              )}

              {/* Stats */}
              <div className="flex items-center space-x-6 mb-6 pb-6 border-b border-gray-200 text-gray-600">
                <div className="flex items-center space-x-2">
                  <Heart className={`w-5 h-5 ${likedPosts.has(selectedPost.id) ? 'text-red-500 fill-current' : ''}`} />
                  <span className="font-medium">{selectedPost.likes}</span>
                </div>
                <div className="flex items-center space-x-2">
                  <MessageCircle className="w-5 h-5" />
                  <span>{(comments[selectedPost.id] || []).length}</span>
                </div>
                <div className="flex items-center space-x-2">
                  <Eye className="w-5 h-5" />
                  <span>{selectedPost.views}</span>
                </div>
              </div>

              {/* Comments Section - Always Visible */}
              <div className="mt-6">
                <h3 className="font-semibold text-gray-900 mb-4 flex items-center">
                  <MessageCircle className="w-5 h-5 mr-2" />
                  댓글 {(comments[selectedPost.id] || []).length}개
                </h3>
                
                {/* Comment Input - Show First */}
                {isAuthenticated && (
                  <div className="flex items-center space-x-2 mb-4">
                    <input
                      value={commentInput}
                      onChange={(e) => setCommentInput(e.target.value)}
                      placeholder="댓글을 입력하세요"
                      className="flex-1 px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-gray-400"
                      onClick={(e) => e.stopPropagation()}
                      onKeyDown={(e) => {
                        // 한글 조합 중 Enter는 무시 — 조합을 끝내는 Enter가 한 번 더 제출돼 마지막 글자만 담긴 댓글이 올라갔다
                        if (e.nativeEvent.isComposing) return;
                        if (e.key === 'Enter' && !e.shiftKey) {
                          e.preventDefault();
                          submitComment(selectedPost.id);
                        }
                      }}
                    />
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        submitComment(selectedPost.id);
                      }}
                      disabled={commentSubmitting || !commentInput.trim()}
                      className="px-4 py-2 bg-gray-900 text-white rounded-md hover:bg-gray-700 whitespace-nowrap disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      {commentSubmitting ? '등록 중' : '등록'}
                    </button>
                  </div>
                )}
                
                {/* Comments List */}
                <div className="space-y-3">
                  {(comments[selectedPost.id] || []).length === 0 ? (
                    <p className="text-gray-500 text-center py-8">첫 댓글을 남겨보세요!</p>
                  ) : (
                    (comments[selectedPost.id] || []).map((c) => (
                      <div key={c.id} className="bg-gray-50 rounded-lg p-4 hover:bg-gray-100 transition-colors">
                        <div className="flex items-center justify-between mb-2">
                          <div className="flex items-center">
                            <span className="font-medium text-gray-800">{c.authorName || '익명'}</span>
                            <span className="text-xs text-gray-500 ml-2" title={c.createdAt}>
                              {formatRelativeTime(c.createdAt)}
                            </span>
                          </div>
                          {/* 댓글 삭제 버튼 (본인 또는 관리자만) */}
                          {isAuthenticated && user && (
                            (Number((c as any).authorId ?? (c as any).author_id) === Number(user.id) || ['admin@pulse.com', 'admin.test@pulse.com'].includes(user.email))
                          ) && (
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                deleteComment(c.id, selectedPost.id);
                              }}
                              className="text-red-500 hover:text-red-700 text-sm"
                              title="댓글 삭제"
                            >
                              <Trash2 className="w-4 h-4" />
                            </button>
                          )}
                        </div>
                        <p className="text-gray-700 leading-relaxed">{c.content}</p>
                      </div>
                    ))
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Login Modal */}
      <LoginModal
        isOpen={showLoginModal}
        notice={loginNotice}
        onClose={() => {
          setLoginNotice('');
          closeLoginModalAndRedirect();
        }}
        onSwitchToSignup={() => {
          closeLoginModalAndRedirect();
          setShowSignupModal(true);
        }}
      />

      {/* Signup Modal */}
      <SignupModal
        isOpen={showSignupModal}
        onClose={() => setShowSignupModal(false)}
        onSwitchToLogin={() => {
          setShowSignupModal(false);
          setShowLoginModal(true);
        }}
      />

      {/* Profile Modal */}
      {showProfileModal && profileUserId && (
        <ProfilePage
          userId={profileUserId}
          isOwnProfile={user?.id === profileUserId}
          onClose={() => {
            setShowProfileModal(false);
            setProfileUserId(null);
          }}
        />
      )}

      {/* AI Consult Modal */}
      {showAiModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-2 sm:p-4">
          <div className="bg-white rounded-xl w-full max-w-3xl max-h-[95vh] sm:max-h-[90vh] overflow-hidden flex flex-col">
            {/* Header — 미니멀: 흰 배경, 얇은 구분선, 텍스트만 */}
            <div className="px-6 sm:px-10 py-4 border-b border-gray-100 flex justify-between items-center">
              <h2 className="text-base font-semibold text-gray-900">AI 의료 상담</h2>
              <button
                onClick={() => setShowAiModal(false)}
                className="text-gray-400 hover:text-gray-600 p-1 transition-colors"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            {/* Content */}
            <div className="flex-1 overflow-y-auto px-6 sm:px-10 py-8">
              <div className="space-y-5">
                {/* 질문 입력 — 내장 제출 버튼 */}
                <div className="relative">
                  <textarea
                    placeholder="임상 질문을 입력하세요"
                    value={aiQuery}
                    onChange={(e) => setAiQuery(e.target.value)}
                    rows={3}
                    className="w-full px-5 py-4 pr-14 border border-gray-300 rounded-2xl focus:outline-none focus:border-gray-900 resize-none text-[15px] leading-6 placeholder-gray-400"
                  />
                  <button
                    onClick={handleAIConsult}
                    disabled={aiLoading || !aiQuery.trim()}
                    title="질문하기"
                    className="absolute right-3 bottom-3.5 w-9 h-9 rounded-lg bg-gray-900 text-white flex items-center justify-center hover:bg-gray-700 disabled:bg-gray-200 disabled:cursor-not-allowed transition-colors"
                  >
                    {aiLoading ? (
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white" />
                    ) : (
                      <ArrowUp className="w-4 h-4" />
                    )}
                  </button>
                </div>

                {/* 옵션 라인 — 근거 모드 · 이미지 첨부 · 글자수 */}
                <div className="flex items-center justify-between text-xs text-gray-500 flex-wrap gap-2">
                  <div className="flex items-center gap-4">
                    {isAuthenticated && (
                      <button
                        onClick={() => setAiEvidenceMode((v) => !v)}
                        className="flex items-center gap-2"
                        title="문헌 근거 안에서만 답하고 모든 주장에 [n] 인용을 표기합니다. 근거가 없으면 답하지 않습니다."
                      >
                        <span
                          className={`relative w-8 h-[18px] rounded-full transition-colors flex-shrink-0 ${
                            aiEvidenceMode ? 'bg-gray-900' : 'bg-gray-300'
                          }`}
                        >
                          <span
                            className={`absolute top-0.5 left-0.5 w-3.5 h-3.5 bg-white rounded-full transition-transform ${
                              aiEvidenceMode ? 'translate-x-3.5' : ''
                            }`}
                          />
                        </span>
                        <span className={aiEvidenceMode ? 'text-gray-900' : ''}>
                          근거 모드 · PubMed 인용{aiImage ? ' (이미지 첨부 시 일반 모드)' : ''}
                        </span>
                      </button>
                    )}
                    {isAuthenticated && (
                      <>
                        <input
                          type="file"
                          accept="image/*"
                          onChange={handleAiImageUpload}
                          className="hidden"
                          id="ai-modal-image-upload"
                        />
                        <label
                          htmlFor="ai-modal-image-upload"
                          className="flex items-center gap-1.5 cursor-pointer hover:text-gray-900 transition-colors"
                        >
                          <Paperclip className="w-3.5 h-3.5" />
                          이미지 첨부
                        </label>
                      </>
                    )}
                  </div>
                  <span className="text-gray-400">{aiQuery.length} / 2000</span>
                </div>

                {/* 이미지 프리뷰 (첨부 시에만, 절제된 크기) */}
                {aiImagePreview && (
                  <div className="flex items-start gap-3">
                    <img
                      src={aiImagePreview}
                      alt="첨부 이미지"
                      className="h-28 rounded-lg border border-gray-200 object-contain bg-gray-50"
                    />
                    <button
                      onClick={() => {
                        setAiImage(null);
                        setAiImagePreview(null);
                      }}
                      className="text-xs text-gray-400 hover:text-gray-700 flex items-center gap-1"
                    >
                      <X className="w-3.5 h-3.5" /> 제거
                    </button>
                  </div>
                )}

                {aiLoading && (
                  <div className="text-xs text-gray-400 animate-pulse">근거를 검색하는 중…</div>
                )}

                {/* 추천·최근 질의 — 응답 전에만, 아웃라인 칩 */}
                {!aiResponse && (
                  <div className="space-y-4 pt-2">
                    <div>
                      <div className="text-[11px] font-medium text-gray-400 uppercase tracking-wide mb-2">추천 질문</div>
                      <div className="flex flex-wrap gap-2">
                        {suggestedPrompts.map((p, idx) => (
                          <button
                            key={idx}
                            onClick={() => setAiQuery(p)}
                            className="px-3.5 py-2 text-[13px] text-gray-600 bg-white border border-gray-200 rounded-full hover:border-gray-400 hover:text-gray-900 transition-colors text-left"
                          >
                            {p}
                          </button>
                        ))}
                      </div>
                    </div>
                    {recentQueries.length > 0 && (
                      <div>
                        <div className="flex items-center justify-between mb-2">
                          <div className="text-[11px] font-medium text-gray-400 uppercase tracking-wide">최근 질의</div>
                          <button
                            onClick={() => {
                              setRecentQueries([]);
                              localStorage.removeItem('ai_recent_queries');
                            }}
                            className="text-[11px] text-gray-400 hover:text-gray-600"
                          >
                            비우기
                          </button>
                        </div>
                        <div className="flex flex-wrap gap-2">
                          {recentQueries.map((rq, idx) => (
                            <button
                              key={idx}
                              onClick={() => setAiQuery(rq)}
                              className="px-3.5 py-2 text-[13px] text-gray-500 bg-white border border-gray-200 rounded-full hover:border-gray-400 hover:text-gray-900 transition-colors max-w-full truncate"
                              title={rq}
                            >
                              {rq}
                            </button>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                )}

                {/* AI 응답 — 문서형: 구분선, 메타 라인, 본문, 참고 문헌, 각주 */}
                {aiResponse && (
                  <div className="border-t border-gray-100 pt-6">
                    <div className="flex items-center justify-between mb-3">
                      <span className="text-xs text-gray-400">
                        {aiInsufficientEvidence
                          ? '근거 부족 — 답변을 생성하지 않았습니다'
                          : aiCitations.length > 0
                          ? `근거 기반 답변 · 인용 ${aiCitations.length}건`
                          : '일반 답변'}
                      </span>
                      <div className="flex items-center gap-3 text-xs text-gray-400">
                        <button onClick={copyAIResponse} className="hover:text-gray-900 flex items-center gap-1">
                          <Copy className="w-3.5 h-3.5" /> {aiCopied ? '복사됨' : '복사'}
                        </button>
                        <button onClick={clearAIInput} className="hover:text-gray-900 flex items-center gap-1">
                          <RotateCcw className="w-3.5 h-3.5" /> 새 질문
                        </button>
                      </div>
                    </div>
                    <div className="text-[15px] leading-7 text-gray-800 whitespace-pre-line">{aiResponse}</div>

                    {aiCitations.length > 0 && (
                      <div className="mt-6">
                        <div className="text-[11px] font-medium text-gray-400 uppercase tracking-wide mb-2">참고 문헌</div>
                        <ol className="space-y-1.5">
                          {aiCitations.map((c: any) => (
                            <li key={c.n} className="text-[13px] leading-relaxed text-gray-600">
                              <a
                                href={c.url}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="hover:text-gray-900 hover:underline"
                              >
                                {c.n}. {c.title} <span className="text-gray-400">{c.journal} ({c.year}). PMID: {c.pmid}</span>
                              </a>
                            </li>
                          ))}
                        </ol>
                      </div>
                    )}

                    <p className="mt-6 text-[11px] leading-relaxed text-gray-400">
                      이 정보는 참고용이며, 실제 진료 시 반드시 종합적 판단이 필요합니다.
                    </p>

                    {isAuthenticated && !aiInsufficientEvidence && (
                      <div className="mt-4">
                        <button
                          onClick={handlePostFromAI}
                          disabled={postSubmitting}
                          className="px-4 py-2 text-[13px] text-gray-600 border border-gray-300 rounded-lg hover:border-gray-500 hover:text-gray-900 disabled:opacity-50 transition-colors flex items-center gap-1.5"
                        >
                          {postSubmitting ? '포스팅 중…' : (<><Plus className="w-3.5 h-3.5" /> 피드에 공유</>)}
                        </button>
                      </div>
                    )}
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      )}
      </>)}
      {/* Footer 제거됨: 내용은 좌측 사이드바 하단으로 이동 */}
      <ApiKeysModal open={apiKeysOpen} onClose={() => setApiKeysOpen(false)} />
    </div>
  );
};

const App = () => {
  return (
    <AuthProvider>
      <AppContent />
    </AuthProvider>
  );
};

export default App;